import { useMemo, type MouseEvent } from "react";
import { EmptyCell } from "../dynamicGrid/columnDefinitions";
import type { GridColDef } from "@mui/x-data-grid-premium";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DynamicGrid } from "../dynamicGrid";
import { EmptyState } from "../shared/EmptyState";
import { PageTitle } from "../shared/Page";
import { HasPermission, usePermission } from "../shared/permissions";
import { RegistrationControllerIcon2 } from "../shared/icons";
import { usePrivacyDocumentTitle } from "../shared/usePrivacyDocumentTitle";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { trackEvent } from "../shared/Snowplow";
import { prefetch } from "../api/prefetch";
import { REGISTRATION_METADATA_FIELDS } from "../api/registrations.types";
import type { PersonalDataControllerListItem } from "../api/personalDataControllers";
import { selectPartnerOptionsQueryOptions } from "../api/partners";
import { enterpriseCustomerOrganisationsQueryOptions } from "../api/enterpriseCustomer";
import { useGetUsersFromCurrentOrganisation } from "../api/users";
import {
  sharedProcessingByIdQueryOptions,
  sharedProcessingFormQueryOptions,
  sharedProcessingsGridViewQueryOptions,
  updateSharedProcessingsGridViewFiltersMutationOptions,
  type SharedProcessingGridRow,
} from "../api/sharedProcessings";
import { CreateSharedProcessingDialog } from "./CreateSharedProcessingDialog";
import { SharedProcessingInlineFormDrawer } from "./SharedProcessingDrawer";
import { SharedProcessingGuidedDrawer } from "./SharedProcessingGuidedDrawer";
import { SharedProcessingHistoryDrawer } from "./SharedProcessingHistoryDrawer";
import { SharedProcessingShareSubDrawer } from "./SharedProcessingShareSubDrawer";
import { SharedProcessingActionMenu } from "./SharedProcessingDrawerHeader";
import { responsibleColumn } from "../shared/responsibleColumn";
import { statusColumn } from "../shared/statusColumn";
import { StatusSquare } from "../shared/RegistrationStatusDot";

const SharedProcessingsGridPage = () => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  usePrivacyDocumentTitle("RegistrationGrid.Shared.Title");
  const gridViewQuery = useQuery(sharedProcessingsGridViewQueryOptions());
  const formQuery = useQuery(sharedProcessingFormQueryOptions());
  const updateFiltersMutation = useMutation(updateSharedProcessingsGridViewFiltersMutationOptions());
  const usersQuery = useGetUsersFromCurrentOrganisation();
  const partnersQuery = useQuery(selectPartnerOptionsQueryOptions());
  const organisationsQuery = useQuery(enterpriseCustomerOrganisationsQueryOptions());
  const { hasPermission } = usePermission();

  return (
    <DynamicGrid.Grid
      rowsData={gridViewQuery.data?.registrations}
      form={formQuery.data}
      getRowId={getRowId}
      asyncQuestionCardData={useMemo(
        () => ({
          usersQueryData: usersQuery.data,
          partnersQueryData: partnersQuery.data,
          enterpriseCustomerOrganisationsQueryData: organisationsQuery.data,
          personalDataControllersQueryData: NO_PERSONAL_DATA_CONTROLLERS_IN_ENTERPRISE_ROOT_SCOPE,
        }),
        [usersQuery.data, partnersQuery.data, organisationsQuery.data]
      )}
      savedFiltersProps={{
        stringifiedFilters: gridViewQuery.data?.stringifiedFilters,
        onChange: stringifiedFilters => {
          trackEvent.changeRegistrationSavedFilters();
          updateFiltersMutation.mutate(stringifiedFilters);
        },
      }}
      onRowSelect={id => navigate(paths.sharedProcessingsInline(id))}
      defaultSort={{ field: REGISTRATION_METADATA_FIELDS.UPDATED_AT, sort: "desc" }}
      Toolbar={SharedProcessingsToolbar}
      Footer={SharedProcessingsGridFooter}
      mainColumnProps={{
        headerName: t("RegistrationGrid.NameHeader"),
        renderLabel: row => row.name,
        renderIcon: ({ iconProps }) => <RegistrationControllerIcon2 {...iconProps} />,
      }}
      getColumns={questionCardColumns => [
        presentInColumn({
          headerName: t("SharedProcessing.PresentIn.Header"),
          organisations: gridViewQuery.data?.organisations ?? [],
          answeredLabel: t("SharedProcessing.InUse"),
          awaitingLabel: t("SharedProcessing.NotInUseYet"),
          notAddedLabel: t("SharedProcessing.PresentIn.NotAdded"),
          onOpen: hasPermission("nova-forms-api:enterprise-customers:registrations:manage")
            ? id => navigate(paths.sharedProcessingsPresentIn(id))
            : undefined,
        }),
        statusColumn(t, undefined, ["draft", "done", "audit"]),
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.CreatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.CREATED_AT,
        }),
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.UpdatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.UPDATED_AT,
        }),
        responsibleColumn({
          headerName: t("RegistrationGrid.ResponsibleHeader"),
          noResponsible: t("RegistrationGrid.NoResponsible"),
        }),
        ...questionCardColumns,
      ]}
      NoRowsOverlay={NoSharedProcessingsOverlay}
      texts={{ searchFieldPlaceholder: t("RegistrationGrid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <SharedProcessingActionMenu
          sharedProcessingId={row.registrationId}
          onDeleted={() => {
            if (selectedRowId === row.registrationId) navigate(paths.sharedProcessingsGrid());
          }}
          onCopied={id => navigate(paths.sharedProcessingsInline(id))}
        />
      )}
    />
  );
};

const getRowId = (row: SharedProcessingGridRow) => row.registrationId;

const NO_PERSONAL_DATA_CONTROLLERS_IN_ENTERPRISE_ROOT_SCOPE: Array<PersonalDataControllerListItem> = [];

const MAX_PRESENT_IN_DOTS = 12;

const PRESENT_IN_FIELD = "presentIn";

const presentInColumn = ({
  headerName,
  organisations,
  answeredLabel,
  awaitingLabel,
  notAddedLabel,
  onOpen,
}: {
  headerName: string;
  organisations: Array<{ organisationId: string; organisationName: string }>;
  answeredLabel: string;
  awaitingLabel: string;
  notAddedLabel: string;
  onOpen: ((id: string) => void) | undefined;
}): GridColDef<SharedProcessingGridRow> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: PRESENT_IN_FIELD }),
  width: 260,
  type: "number",
  align: "left",
  headerAlign: "left",
  valueGetter: (_, row) => row.presentIn?.length ?? 0,
  renderCell: ({ row }) => {
    const has = new Set(row.presentIn);
    const awaiting = new Set(row.awaitingIn);
    const stateOf = (organisation: { organisationId: string }) =>
      !has.has(organisation.organisationId)
        ? "notAdded"
        : awaiting.has(organisation.organisationId)
          ? "awaiting"
          : "answered";
    const ordered = (["answered", "awaiting", "notAdded"] as const).flatMap(state =>
      organisations.filter(organisation => stateOf(organisation) === state)
    );
    const labels = { answered: answeredLabel, awaiting: awaitingLabel, notAdded: notAddedLabel };
    const shown = ordered.slice(0, MAX_PRESENT_IN_DOTS);
    const hidden = ordered.slice(MAX_PRESENT_IN_DOTS);
    const describe = (organisation: { organisationId: string; organisationName: string }) =>
      `${organisation.organisationName}: ${labels[stateOf(organisation)]}`;

    if (has.size === 0) {
      return (
        <DynamicGrid.Cell withExtraSpacingTop={false}>
          <EmptyCell />
        </DynamicGrid.Cell>
      );
    }

    return (
      <DynamicGrid.Cell withExtraSpacingTop={false}>
        <Box
          component={onOpen ? "button" : "span"}
          type={onOpen ? "button" : undefined}
          onClick={
            onOpen &&
            ((event: MouseEvent) => {
              event.stopPropagation();
              onOpen(row.registrationId);
            })
          }
          sx={{
            "all": "unset",
            "cursor": onOpen ? "pointer" : "default",
            "display": "flex",
            "alignItems": "center",
            "gap": 0.75,
            "flexWrap": "wrap",
            "borderRadius": 0.5,
            "&:focus-visible": { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
          }}
        >
          {shown.map(organisation => (
            <Tooltip key={organisation.organisationId} title={describe(organisation)}>
              <StatusSquare
                sx={theme =>
                  ({
                    answered: { bgcolor: theme.palette.success.main },
                    awaiting: { bgcolor: theme.palette.brandOrange["500"] },
                    notAdded: { border: `1px solid ${theme.palette.grey["400"]}` },
                  })[stateOf(organisation)]
                }
              />
            </Tooltip>
          ))}
          {hidden.length > 0 && (
            <Tooltip
              title={
                <Stack>
                  {ordered.map(organisation => (
                    <span key={organisation.organisationId}>{describe(organisation)}</span>
                  ))}
                </Stack>
              }
            >
              <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}>
                +{hidden.length}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </DynamicGrid.Cell>
    );
  },
});

const SharedProcessingsGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="RegistrationGrid.NumberOfRows" />;

const NoSharedProcessingsOverlay = () => <EmptyState labelKey="RegistrationGrid.NoRows" />;

const SharedProcessingsToolbar = () => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Toolbar
      pageTitle={
        <PageTitle title={t("SharedProcessing.Library.OwnTitle")} subTitle={t("RegistrationGrid.Shared.SubTitle")} />
      }
      actionButtons={
        <HasPermission permission="nova-forms-api:enterprise-customers:registrations:manage">
          <DynamicGrid.CreateNewButton
            labelKey="CreateRegistration.Button"
            renderDialog={({ onClose }) => (
              <CreateSharedProcessingDialog
                onClose={onClose}
                onCreated={id => navigate(paths.sharedProcessingsInline(id))}
              />
            )}
          />
        </HasPermission>
      }
    />
  );
};

export const prefetchSharedProcessingsGridPage = () => {
  prefetch(sharedProcessingsGridViewQueryOptions());
  prefetch(sharedProcessingFormQueryOptions());
  prefetch(enterpriseCustomerOrganisationsQueryOptions());
};

export const sharedProcessingsGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <SharedProcessingsGridPage />,
  rowDrawerRoutes: [
    { index: true, element: <SharedProcessingInlineFormDrawer /> },
    { path: "guided/*", element: <SharedProcessingGuidedDrawer /> },
    { path: "history", element: <SharedProcessingHistoryDrawer /> },
    { path: "share", element: <SharedProcessingShareSubDrawer /> },
  ],
  gridPrefetcher: prefetchSharedProcessingsGridPage,
  rowDrawerDefaultPrefetcher: ({ rowId }) => void prefetch(sharedProcessingByIdQueryOptions(rowId)),
});
